import PropTypes from 'prop-types';
import _ from 'lodash';
import Statuses from '../../../../model/Statuses';
import nodeSortingOptionsSequence from '../../../utils/nodeSortingOptionsSequence';
import makeTreePlugins from '../DynamicTree/internal/makeTreePlugins';
import selectionModes from '../DynamicTree/internal/selectionModes';
import makeStatusMatcher from '../StaticTree/internal/makeStatusMatcher';
import CommonAdapterTree from './CommonAdapterTree';

class CommonStaticTree extends CommonAdapterTree {
  static propTypes = {
    ...CommonAdapterTree.propTypes,

    staticModels: PropTypes.array,

    filter: PropTypes.shape({
      value: PropTypes.object,
      component: PropTypes.func,
      onSelectionChange: PropTypes.func,
      hasSearchFieldOnly: PropTypes.bool,
      predicatesMeta: PropTypes.arrayOf(
        PropTypes.shape({
          valuePropName: PropTypes.string.isRequired,
          predicate: PropTypes.func.isRequired,
          includeChildren: PropTypes.bool,
          collectVisibleIds: PropTypes.bool,
        }),
      ),
    }),

    plugins: PropTypes.shape({
      addNodeBuildMeta: PropTypes.func,
      nodeIsVisible: PropTypes.func,
      nodeSortingOptions: PropTypes.shape({
        iteratees: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.func,
          PropTypes.arrayOf(
            PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
          ),
        ]),
        orders: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
      }),
      nodeIsSelectable: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
      resolveNodePopoverText: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
        PropTypes.func,
      ]),
      resolveNodeIconName: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
        PropTypes.object,
        PropTypes.func,
      ]),
      nodeTotalLeafCounterOptions: PropTypes.shape({
        isEnabled: PropTypes.bool,
        autoShow: PropTypes.bool,
      }),
      lazyLoadingOptions: PropTypes.shape({
        reloadSeedData: PropTypes.bool,
        nodeCanLoadChildren: PropTypes.func.isRequired,
        createObservableQuery: PropTypes.func.isRequired,
      }),
      hasDynamicLeaves: PropTypes.bool,
    }),
  };

  static defaultProps = {
    ...CommonAdapterTree.defaultProps,

    staticModels: [],

    filter: {
      value: null,
      component: null,
      onChange: null,
      predicatesMeta: null,
      hasSearchFieldOnly: false,
      hasOmitDeleteStatus: false,
    },

    plugins: {},
  };

  hasDynamicLeaves = true;
  nodeIsSelectable = true;
  nodeSortingOptions = nodeSortingOptionsSequence;

  get rootModels() {
    return this.state.rootModels;
  }

  get models() {
    const { staticModels } = this.props;
    const { rootModels } = this.state;

    return staticModels && staticModels.length
      ? [...rootModels, ...staticModels]
      : [...rootModels];
  }

  get inflateInitialExpansion() {
    const { nodeExpansionOptions } = this.props;

    return nodeExpansionOptions &&
      nodeExpansionOptions.hasOwnProperty('inflateInitialExpansion')
      ? nodeExpansionOptions.inflateInitialExpansion
      : undefined;
  }

  get nodeShouldBeInitiallyVisible() {
    const { nodeExpansionOptions } = this.props;

    return (
      nodeExpansionOptions && nodeExpansionOptions.nodeShouldBeInitiallyVisible
    );
  }

  predicatesMeta = () => {
    const {
      adapter,
      filter: propsFilter,
      filter: { predicatesMeta },
      value,
      staticModels,
    } = this.props;

    if (predicatesMeta) {
      return predicatesMeta;
    }

    const {
      filter: stateFilter,
      filter: { searchQuery },
    } = this.state;

    const filter = _.isNil(propsFilter.value) ? stateFilter : propsFilter;

    return [
      {
        valuePropName: 'status',
        predicate: makeStatusMatcher(adapter, value, staticModels),
        includeChildren: !Statuses.isActive(filter),
        includeParents: !Statuses.isActive(filter),
        collectVisibleIds: !searchQuery,
      },
      {
        valuePropName: 'searchQuery',
        predicate: this.makeNameQueryMatcher,
        includeChildren: true,
      },
    ];
  };

  nodeIsVisible = () => true;

  makePlugins = () => {
    const { selectionMode, plugins } = this.props;

    const {
      addNodeBuildMeta,
      resolveNodePopoverText,
      resolveNodeIconName,
      nodeTotalLeafCounterOptions,
      lazyLoadingOptions,

      nodeIsVisible = this.nodeIsVisible,
      nodeSortingOptions = this.nodeSortingOptions,
      nodeIsSelectable = this.nodeIsSelectable,
      hasDynamicLeaves = this.hasDynamicLeaves,
    } = plugins;

    return makeTreePlugins({
      addNodeBuildMeta,
      nodeIsVisible,
      nodeSortingOptions,
      nodeIsSelectable,
      lazyLoadingOptions,
      hasDynamicLeaves,
      nodeTotalLeafCounterOptions,
      resolveNodeIconName,
      resolveNodePopoverText,

      resolveNodeClassName: this.resolveNodeClassName,
      resolveNodeCustomText: this.resolveNodeCustomText,
      getNodeParentId: this.resolveNodeParentId,

      selectionMode: selectionModes[selectionMode],
      nodeExpansionOptions: { isDisabled: true },
    });
  };
}

export default CommonStaticTree;
